import React from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import type { VirtualBucket, Backend, Credential, Account } from '@/types'

interface Props {
  backend: Backend
  virtualBucket: VirtualBucket
  credentials: Credential[]
}

export default function VirtualBucketsShow({ backend, virtualBucket, credentials }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const handleDelete = () => {
    router.delete(`/virtual_buckets/${virtualBucket.id}`)
  }

  return (
    <ManagementLayout>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
        <Link href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}>{backend.name}</Link>
        <Typography color="text.primary">{virtualBucket.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" fontWeight={600}>
          {virtualBucket.name}
        </Typography>
        {isAccountAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              href={`/virtual_buckets/${virtualBucket.id}/edit`}
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Base Path</Typography>
                {virtualBucket.base_path ? (
                  <Box component="code" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 0.5, fontSize: '0.875rem' }}>
                    {virtualBucket.base_path}
                  </Box>
                ) : (
                  <Typography variant="body2">Root (no prefix)</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Public Access</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={virtualBucket.public_read ? 'Public Read' : 'Private Read'}
                    size="small"
                    color={virtualBucket.public_read ? 'success' : 'default'}
                  />
                  <Chip
                    label={virtualBucket.public_list ? 'Public List' : 'Private List'}
                    size="small"
                    color={virtualBucket.public_list ? 'info' : 'default'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Assigned Credentials
            </Typography>
            {credentials.length > 0 ? (
              <List disablePadding>
                {credentials.map((cred, index) => (
                  <ListItem key={cred.id} divider={index < credentials.length - 1} disableGutters>
                    <ListItemText
                      primary={cred.name}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Box component="code" sx={{ bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.75rem' }}>
                            {cred.access_key_id}
                          </Box>
                          <Chip
                            label={cred.permissions === 'read_write' ? 'R/W' : cred.permissions === 'read_only' ? 'Read' : 'Write'}
                            size="small"
                            color={cred.permissions === 'read_write' ? 'primary' : cred.permissions === 'read_only' ? 'success' : 'warning'}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No credentials assigned to this virtual bucket.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Virtual Bucket"
        message={`Are you sure you want to delete "${virtualBucket.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </ManagementLayout>
  )
}
